import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, fmt } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, clear, lineKey } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [form, setForm] = useState({ customer_name: "", email: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(sessionId ? "verifying" : "form"); // form | verifying | paid | failed
  const polled = useRef(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const shipping = subtotal > 150 || subtotal === 0 ? 0 : 12;
  const total = subtotal + shipping;

  const poll = useCallback(async (sid, attempt = 0) => {
    if (attempt >= 8) { setStatus("failed"); return; }
    try {
      const r = await api.get(`/checkout/status/${sid}`);
      if (r.data.payment_status === "paid") { setStatus("paid"); clear(); return; }
      if (r.data.status === "expired") { setStatus("failed"); return; }
    } catch { /* retry */ }
    setTimeout(() => poll(sid, attempt + 1), 2000);
  }, [clear]);

  useEffect(() => {
    if (sessionId && !polled.current) { polled.current = true; poll(sessionId); }
  }, [sessionId, poll]);

  const pay = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.address) { toast("Please fill in all fields"); return; }
    setBusy(true);
    try {
      const r = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        customer_name: form.customer_name, email: form.email, address: form.address,
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity })),
      });
      window.location.href = r.data.url;
    } catch {
      toast("Could not start checkout. Try again.");
      setBusy(false);
    }
  };

  if (status === "verifying") {
    return <div data-testid="checkout-verifying" className="pt-40 text-center pb-32 caption text-sm">Confirming your payment…</div>;
  }

  if (status === "paid") {
    return (
      <div data-testid="order-success" className="pt-40 max-w-2xl mx-auto px-6 text-center pb-32">
        <p className="util-link text-[11px] caption mb-4">Order Confirmed</p>
        <h1 className="font-display text-5xl sm:text-6xl uppercase mb-5">Merci</h1>
        <p className="caption text-sm leading-relaxed">Your payment was successful and your order is confirmed. A confirmation has been sent to your email.</p>
        <div className="flex gap-3 justify-center mt-10">
          <Link to="/shop" data-testid="success-continue" className="bg-black text-white px-9 py-4 product-title text-[13px] link-hover">Continue Shopping</Link>
          {user && <Link to="/account" className="border border-black px-9 py-4 product-title text-[13px] link-hover">View Orders</Link>}
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div data-testid="checkout-failed" className="pt-40 max-w-2xl mx-auto px-6 text-center pb-32">
        <h1 className="font-display text-4xl uppercase mb-5">Payment not completed</h1>
        <p className="caption text-sm mb-8">Your payment was cancelled or could not be verified. No charge was made.</p>
        <button onClick={() => { setStatus("form"); nav("/checkout"); }} className="bg-black text-white px-9 py-4 product-title text-[13px] link-hover">Try Again</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="pt-40 text-center pb-32">
        <h1 className="font-display text-4xl uppercase mb-6">Your bag is empty</h1>
        <Link to="/shop" className="bg-black text-white px-9 py-4 product-title text-[13px]">Shop the Collection</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="pt-16">
      <div className="px-6 md:px-10 lg:px-20 pt-16 pb-28 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <h1 className="font-display text-5xl uppercase mb-4">Checkout</h1>
          {!user && (
            <p className="caption text-[12px] mb-8"><Link to="/login" className="text-black underline link-hover">Log in</Link> to save this order to your account.</p>
          )}
          <form onSubmit={pay} className="space-y-8">
            {[
              { k: "customer_name", l: "Full Name", t: "text", tid: "checkout-name" },
              { k: "email", l: "Email", t: "email", tid: "checkout-email" },
            ].map((f) => (
              <div key={f.k}>
                <label className="util-link text-[11px] caption">{f.l}</label>
                <input data-testid={f.tid} type={f.t} value={form[f.k]}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="w-full mt-2 bg-transparent border-b border-border py-3 focus:border-black outline-none text-sm" />
              </div>
            ))}
            <div>
              <label className="util-link text-[11px] caption">Shipping Address</label>
              <textarea data-testid="checkout-address" rows={3} value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full mt-2 bg-transparent border-b border-border py-3 focus:border-black outline-none resize-none text-sm" />
            </div>
            <button data-testid="place-order" disabled={busy}
              className="w-full bg-black text-white py-4 product-title text-[13px] link-hover disabled:opacity-50">
              {busy ? "Redirecting to payment…" : `Pay with Card — ${fmt(total)}`}
            </button>
            <p className="text-center text-xs caption">Secure payment via Stripe.</p>
          </form>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-card p-8">
            <h2 className="product-title text-[13px] mb-6">Order Summary</h2>
            <div className="space-y-5 mb-6">
              {items.map((i) => (
                <div key={lineKey(i)} className="flex gap-4">
                  <img src={i.image} alt={i.name} className="w-16 h-20 object-cover bg-white" />
                  <div className="flex-1">
                    <p className="product-title text-[12px]">{i.name}</p>
                    <p className="text-xs caption">{[i.size, i.color].filter(Boolean).join(" · ")}</p>
                    <p className="text-sm mt-1">Qty {i.quantity}</p>
                  </div>
                  <span className="text-sm">{fmt(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="caption">Subtotal</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="caption">Shipping</span><span>{shipping === 0 ? "Free" : fmt(shipping)}</span></div>
              <div className="flex justify-between text-lg pt-3"><span>Total</span><span data-testid="checkout-total">{fmt(total)}</span></div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
